import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/styles';
import { useCallback } from 'react';

export interface IShowCaseStep {
  title: string;
  image?: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  primaryButtonHandleClick?(): void;
  secondaryButtonHandleClick?(): void;
}

interface IProps {
  steps: IShowCaseStep[];
  currentStep: number;
  open: boolean;
  onClose(): void;
}

const useStyles = makeStyles({
  dialog: {
    zIndex: 99999999999
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    padding: '24px 24px 32px',
    '& h6': {
      fontSize: '1rem',
      color: '#37474F',
      fontFamily: 'Open Sans',
      fontWeight: 600,
      padding: 0
    }
  },
  steps: {
    fontFamily: 'Open Sans',
    fontWeight: 600,
    color: '#546E7A',
    padding: '10px 24px 24px 24px'
  },
  description: {
    paddingTop: 32,
    fontFamily: 'Open Sans',
    fontWeight: 400,
    color: '#546E7A',
    lineHeight: '24px'
  },
  actions: {
    justifyContent: 'space-between'
  },
  primaryButton: {
    backgroundColor: '#0D47A1',
    color: '#FFF',
    fontFamily: 'Open Sans',
    textTransform: 'none',
    fontWeight: 600,
    margin: '0 24px 24px 24px',
    boxShadow: 'none',
    '&:hover': {
      background: '#07224b'
    }
  },
  secondaryButton: {
    color: '#0D47A1',
    fontFamily: 'Open Sans',
    textTransform: 'none',
    fontWeight: 600,
    margin: '0 24px 24px 24px'
  }
});

function ShowCaseDialog({ steps, currentStep, open, onClose }: IProps) {
  const classes = useStyles();

  const handleClickPrimaryButton = useCallback(() => {
    steps[currentStep].primaryButtonHandleClick();
  }, [steps, currentStep]);

  const handleClickSecondaryButton = useCallback(() => {
    steps[currentStep].secondaryButtonHandleClick();
  }, [steps, currentStep]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      keepMounted
      onClose={onClose}
      aria-describedby="showcase-dialog-description"
    >
      <div className={classes.header}>
        <DialogTitle className={classes.title}>{steps[currentStep].title}</DialogTitle>
        <span className={classes.steps}>{currentStep + 1}/2</span>
      </div>

      <DialogContent>
        <img src={steps[currentStep].image} alt={steps[currentStep].title} />
        <DialogContentText className={classes.description} id="showcase-dialog-description">
          {steps[currentStep].description}
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button className={classes.secondaryButton} onClick={handleClickSecondaryButton}>
          {steps[currentStep].secondaryButtonText}
        </Button>
        <Button variant="contained" className={classes.primaryButton} onClick={handleClickPrimaryButton}>
          {steps[currentStep].primaryButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShowCaseDialog;
